import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pill, RefreshCw, Loader2, ChevronDown, ChevronUp, Database } from 'lucide-react';

const API = `${''}/api`;

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR') : '—');

export const BdpmAdminPanel = () => {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/bdpm/status`);
      setMeta(res.data.meta || null);
    } catch (e) {
      toast.error('Erreur chargement du statut BDPM');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/admin/bdpm/refresh`);
      toast.success('Rafraîchissement BDPM lancé — mise à jour dans ~30 s');
      const before = meta?.imported_at;
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const res = await axios.get(`${API}/admin/bdpm/status`);
          if (res.data.meta?.imported_at && res.data.meta.imported_at !== before) {
            setMeta(res.data.meta);
            toast.success(`BDPM à jour : ${res.data.meta.specialites_count} spécialités`);
            clearInterval(poll);
            setRefreshing(false);
          }
        } catch (e) { /* ignore */ }
        if (tries >= 12) { clearInterval(poll); setRefreshing(false); }
      }, 5000);
    } catch (e) {
      toast.error('Erreur lors du rafraîchissement');
      setRefreshing(false);
    }
  };

  return (
    <div data-testid="bdpm-admin-panel" className="rounded-2xl border border-slate-700 bg-slate-900/40 p-4">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3" data-testid="bdpm-admin-toggle">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center shrink-0">
            <Pill className="w-5 h-5 text-white" />
          </div>
          <div className="text-left min-w-0">
            <h3 className="text-white font-semibold flex items-center gap-2">
              Base Médicaments (BDPM)
              <span className="inline-flex items-center gap-1 text-[11px] text-cyan-300 bg-cyan-500/10 px-2 py-0.5 rounded-full">
                <Database className="w-3 h-3" /> Officielle ANSM/HAS
              </span>
            </h3>
            <p className="text-xs text-slate-400 truncate">
              {meta ? `Base du ${meta.version_date} • ${meta.specialites_count?.toLocaleString('fr-FR')} spécialités` : 'Source réelle des médicaments'}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />}
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-800/40 rounded-xl p-3">
                <p className="text-slate-500 text-xs">Version de la base</p>
                <p className="text-white font-medium">{meta?.version_date || '—'}</p>
              </div>
              <div className="bg-slate-800/40 rounded-xl p-3">
                <p className="text-slate-500 text-xs">Spécialités</p>
                <p className="text-white font-medium">{meta?.specialites_count?.toLocaleString('fr-FR') || '—'}</p>
              </div>
              <div className="bg-slate-800/40 rounded-xl p-3">
                <p className="text-slate-500 text-xs">Présentations</p>
                <p className="text-white font-medium">{meta?.presentations_count?.toLocaleString('fr-FR') || '—'}</p>
              </div>
              <div className="bg-slate-800/40 rounded-xl p-3">
                <p className="text-slate-500 text-xs">Dernier import</p>
                <p className="text-white font-medium text-xs">{fmtDateTime(meta?.imported_at)}</p>
              </div>
            </div>
          )}

          <p className="text-xs text-slate-500">
            Rafraîchissement automatique semestriel (avec la cotation). La BDPM officielle est actualisée mensuellement — utilisez le bouton ci-dessous pour une mise à jour immédiate.
          </p>

          <button
            onClick={refresh}
            disabled={refreshing}
            data-testid="bdpm-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm disabled:opacity-50"
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {refreshing ? 'Mise à jour en cours…' : 'Rafraîchir maintenant'}
          </button>
        </div>
      )}
    </div>
  );
};
